/**
 * Audit entries carry a "safe before/after summary" in `changes`. Most are `{ before?, after? }`
 * objects; some are a flat object of facts (e.g. `{ method, path }`). These helpers turn either
 * shape into rows for a before/after table.
 */

export interface DiffRow {
  field: string;
  before: string;
  after: string;
  changed: boolean;
}

export type ChangeView =
  | { kind: 'none' }
  | { kind: 'diff'; rows: DiffRow[] }
  | { kind: 'values'; rows: { field: string; value: string }[] };

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

/** One value as display text: `—` for null/undefined, JSON for objects and arrays. */
export function displayValue(v: unknown): string {
  if (v === null || v === undefined || v === '') return '—';
  if (typeof v === 'string') return v;
  if (typeof v === 'number' || typeof v === 'boolean' || typeof v === 'bigint') return String(v);
  if (Array.isArray(v) && v.every((x) => typeof x === 'string' || typeof x === 'number'))
    return v.length ? v.join(', ') : '—';
  return JSON.stringify(v);
}

/** "paymentTermsDays" → "Payment terms days"; "is_active" → "Is active". */
export function fieldLabel(key: string): string {
  const spaced = key
    .replace(/[_.-]+/g, ' ')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .trim()
    .toLowerCase();
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

export function describeChanges(changes: unknown): ChangeView {
  if (changes === null || changes === undefined) return { kind: 'none' };
  if (!isRecord(changes))
    return { kind: 'values', rows: [{ field: 'Value', value: displayValue(changes) }] };
  const keys = Object.keys(changes);
  if (keys.length === 0) return { kind: 'none' };

  const hasBeforeAfter = 'before' in changes || 'after' in changes;
  // A new record: only "after" values, nothing to compare against.
  if (
    hasBeforeAfter &&
    (changes.before === undefined || changes.before === null) &&
    isRecord(changes.after)
  ) {
    const after = changes.after;
    return {
      kind: 'values',
      rows: Object.keys(after).map((k) => ({
        field: fieldLabel(k),
        value: displayValue(after[k]),
      })),
    };
  }

  if (hasBeforeAfter) {
    const before = changes.before;
    const after = changes.after;
    const extra = keys.filter((k) => k !== 'before' && k !== 'after');
    const rows: DiffRow[] = [];
    if (isRecord(before) || isRecord(after)) {
      const b = isRecord(before) ? before : {};
      const a = isRecord(after) ? after : {};
      const fields = [...new Set([...Object.keys(b), ...Object.keys(a)])];
      for (const f of fields) {
        const bv = displayValue(isRecord(before) ? b[f] : undefined);
        const av = displayValue(isRecord(after) ? a[f] : undefined);
        rows.push({ field: fieldLabel(f), before: bv, after: av, changed: bv !== av });
      }
    } else {
      const bv = displayValue(before);
      const av = displayValue(after);
      rows.push({ field: 'Value', before: bv, after: av, changed: bv !== av });
    }
    for (const k of extra) {
      const v = displayValue(changes[k]);
      rows.push({ field: fieldLabel(k), before: '—', after: v, changed: false });
    }
    // Changed fields first, keeping their original order.
    rows.sort((x, y) => Number(y.changed) - Number(x.changed));
    return { kind: 'diff', rows };
  }

  return {
    kind: 'values',
    rows: keys.map((k) => ({ field: fieldLabel(k), value: displayValue(changes[k]) })),
  };
}

/** "sales_invoice.post" → "Sales invoice post"; used for action and entity labels. */
export function actionLabel(action: string): string {
  return fieldLabel(action.replace(/\./g, ' '));
}
